import { CustomerSiteOption } from '@/api/customers/types';
import { PaginatedResponse } from '@/api/types';
import { CustomerUserResponse } from '@/api/users/types';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { Fragment, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Customer, CustomerUser } from '@/core/entities';
import { useRoles, useRolesHierarchy } from '@/queries/users';
import { Actions } from '@/components/ui/table/types';
import { Roles } from '@/core/types';
import useToastMessage from '@/shared/context/toast-message-context';
import { useExportSiteUsers } from '@/queries/sites';
import { downloadFile } from '@/shared/utils/file';
import { ALL_SITES } from '../customer-users-table/CustomerUsersTableHeader';
import { UserFormModel } from '../forms/user-form/types';
import {
	initialCustomerUserFormModel,
	userFormModelFromCustomerUser,
} from '../forms/user-form/user-form.defaults';
import CustomerUserManagementBase from './CustomerUserManagementBase';
import UserForm from '../forms/user-form/UserForm';
import DeleteUserForm from '../forms/DeleteUserForm';

interface CustomerUserManagementProps {
	query: UseInfiniteQueryResult<PaginatedResponse<CustomerUserResponse>, unknown>;
	invalidateQuery: () => Promise<void> | void;
	siteOptions?: CustomerSiteOption[];
	customer?: Customer | null;
	exportUsersSiteId: string | undefined;
}

const CustomerUserManagement: FunctionComponent<CustomerUserManagementProps> = ({
	query,
	invalidateQuery,
	siteOptions,
	customer,
	exportUsersSiteId,
}): ReactElement => {
	const { dispatch } = useToastMessage();
	// User form
	const [userForm, setUserForm] = useState<{
		open: boolean;
		formModel: UserFormModel;
		user?: CustomerUser;
	}>({
		open: false,
		formModel: initialCustomerUserFormModel,
	});

	// Delete form
	const [deleteUserForm, setDeleteUserForm] = useState<{ open: boolean; model?: CustomerUser }>({
		open: false,
		model: undefined,
	});

	const { data: rolesOptions } = useRoles();
	const { data: rolesHierarchy } = useRolesHierarchy();

	const [users, setUsers] = useState<CustomerUser[]>([]);

	const { data: userPages, isLoading } = query;

	const exportUsersSiteName = useMemo(
		() => siteOptions?.find((site) => site.id === exportUsersSiteId)?.name ?? 'site',
		[siteOptions, exportUsersSiteId]
	);

	useEffect(() => {
		const userPage = userPages?.pages[0];
		if (userPage) {
			setUsers(userPage.data);
		}
	}, [userPages]);

	const isUserFormDisabled: boolean = useMemo(() => {
		const { firstName, lastName, email, roleId, sites } = userForm.formModel;
		const isDisabled = !firstName.length || !lastName.length || !roleId.length || !sites?.length;

		const operatorRoleId = rolesOptions?.find((role) => role.title === Roles.OPERATOR)?.id;
		if (roleId === operatorRoleId) {
			return isDisabled;
		}

		return isDisabled || !email || !email.length;
	}, [userForm.formModel, rolesOptions]);

	const hasPermissionForAction = (user: CustomerUser) =>
		!!rolesHierarchy?.map((role) => role.id).includes(user.roleId);

	const { mutate: onExportUsers } = useExportSiteUsers({
		onSuccess: (data: string) => {
			const fileName = `${exportUsersSiteName.toLowerCase().split(' ').join('-')}-users.csv`;
			downloadFile(data, fileName);
		},
		onError: (error) => {
			const { statusCode, message } = error?.response?.data || {};
			if (statusCode === 400) {
				dispatch({ type: 'error', content: message });
			}
		},
	});

	return (
		<Fragment>
			<CustomerUserManagementBase
				table={{
					data: users,
					roles: rolesOptions,
					isLoading,
					actions: [
						{
							type: Actions.EDIT,
							permitted: hasPermissionForAction,
							callback: (user: CustomerUser) =>
								setUserForm({
									formModel: userFormModelFromCustomerUser(user),
									user,
									open: true,
								}),
						},
						{
							type: Actions.DELETE,
							permitted: hasPermissionForAction,
							callback: (user: CustomerUser) => setDeleteUserForm({ model: user, open: true }),
						},
					],
				}}
				header={{
					customer,
					setUserForm: () =>
						setUserForm({
							...userForm,
							user: undefined,
							open: true,
							formModel: initialCustomerUserFormModel,
						}),
					exportUsers:
						exportUsersSiteId && exportUsersSiteId !== ALL_SITES
							? () => onExportUsers(exportUsersSiteId)
							: undefined,
				}}
			/>
			<UserForm
				open={userForm.open}
				onClose={() => setUserForm({ ...userForm, open: false })}
				formModel={userForm.formModel}
				updateFormModel={(value: UserFormModel) =>
					setUserForm({ ...userForm, formModel: { ...userForm.formModel, ...value } })
				}
				disabled={isUserFormDisabled}
				user={userForm.user}
				customer={customer}
				onSubmitted={invalidateQuery}
				sites={siteOptions}
			/>
			<DeleteUserForm
				open={deleteUserForm.open}
				onClose={() => setDeleteUserForm({ ...deleteUserForm, open: false })}
				user={deleteUserForm.model}
				onSubmitted={invalidateQuery}
				customerName={customer?.name}
			/>
		</Fragment>
	);
};

export default CustomerUserManagement;
